/*
 * Copyright (c) 2019. ProVision Media Group Ltd. <https://provision.bg>
 * Venelin Iliev <http://veneliniliev.com>
 */

import { NgModule } from '@angular/core';
import { CommonModule } from '@angular/common';

import { LoaderComponent } from './components/loader/loader.component';
import { MomentModule } from 'ngx-moment';
import { ExcelModule, GridModule } from '@progress/kendo-angular-grid';
import { ToastNoAnimationModule } from 'ngx-toastr';
import { TranslateModule } from '@ngx-translate/core';
import { FormValidationMessageComponent } from './components/forms/form-validation-message/form-validation-message.component';
import { SweetAlert2Module } from '@sweetalert2/ngx-sweetalert2';
import { EditButtonComponent } from './components/edit-button/edit-button.component';
import { DeleteButtonComponent } from './components/delete-button/delete-button.component';
import { ActiveIndicatorComponent } from './components/active-indicator/active-indicator.component';
import { CountriesSelectComponent } from './components/countries-select/countries-select.component';
import { NgSelectModule } from '@ng-select/ng-select';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { ActivitiesComponent } from './components/activities/activities.component';
import { AccordionModule } from 'primeng/accordion';
import { AutoCompleteModule } from 'primeng/autocomplete';
import { CalendarModule } from 'primeng/calendar';
import { ColorPickerModule } from 'primeng/colorpicker';
import { FileUploadModule } from 'primeng/fileupload';
import { InputTextareaModule } from 'primeng/inputtextarea';
import { PaginatorModule } from 'primeng/paginator';
import { SidebarModule } from 'primeng/sidebar';
import { TabViewModule } from 'primeng/tabview';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { DataViewModule } from 'primeng/dataview';
import { NgScrollbarModule } from 'ngx-scrollbar';
import { DiffMatchPatchModule } from 'ng-diff-match-patch/dist/ng-diff-match-patch';
import { MediaLibraryComponent } from './components/media-library/media-library/media-library.component';
import { MediaLibraryButtonComponent } from './components/media-library/media-library-button/media-library-button.component';
import { MediaLibraryService } from '@app/shared/components/media-library/media-library.service';
import { ContactPersonComponent } from './components/contact-person/contact-person.component';
import { ContactComponent } from './components/contact/contact.component';
import { TypeSelectComponent } from './components/type-select/type-select.component';
import { PropertyComponent } from './components/property/property.component';
// import { AgmCoreModule } from '@agm/core';
import { GoogleMapsModule } from '@angular/google-maps';
import { CitiesSelectComponent } from './components/cities-select/cities-select.component';
import { NgxGoogleAddressAutocompleteModule } from 'ngx-google-address-autocomplete';
import { BankAccountsComponent } from './components/bank-accounts/bank-accounts.component';
import { VenueSelectComponent } from './components/venue-select/venue-select.component';
import { VenueDistanceComponent } from './components/venue-distance/venue-distance.component';
import { KendoGridService } from '@app/shared/kendo/kendo-grid.service';
import { AuthenticationService } from '@app/core';
import { VenuesInRangeComponent } from './components/venues-in-range/venues-in-range.component';
// TODO: NgbButtonsModule has been deprecated and it was used to manage button toggles
// we should use standard Bootstrap classes for button toggles instead.
import { NgbModule, NgbPopoverModule, NgbTooltipModule } from '@ng-bootstrap/ng-bootstrap';
import { IndustrySelectComponent } from './components/industry-select/industry-select.component';
import { DatePickerComponent } from './components/date-picker/date-picker.component';
import { EventGroupSelectComponent } from './components/event-group-select/event-group-select.component';
import { HotelSelectComponent } from './components/hotel-select/hotel-select.component';
import { SupplierSelectComponent } from './components/supplier-select/supplier-select.component';
import { CompanySelectComponent } from './components/company-select/company-select.component';
import { EventsSelectComponent } from './components/events-select/events-select.component';
import { CurrencySelectComponent } from './components/currency-select/currency-select.component';
import { CurrencyConverterComponent } from './components/currency-converter/currency-converter.component';
import { NightStatusComponent } from './components/night-status/night-status.component';
import { CommentsComponent } from './components/comments/comments.component';
import { ActivitiesButtonComponent } from '@app/shared/components/activities-button/activities-button.component';
import { TimePickerComponent } from './components/time-picker/time-picker.component';
import { UserBoxComponent } from './components/user-box/user-box.component';
import { ClientSelectComponent } from './components/client-select/client-select.component';
import { ClientFinderComponent } from './components/client-finder/client-finder.component';
import { NgxFilesizeModule } from 'ngx-filesize';
// todo: deprecated
// https://v17.angular.io/tutorial/first-app/first-app-lesson-13
// import { Ng2SearchPipeModule } from 'ng2-search-filter';
import { SeoButtonComponent } from './components/seo-button/seo-button.component';
import { PreviewButtonComponent } from './components/preview-button/preview-button.component';
import { EventSelectComponent } from './components/event-select/event-select.component';
import { UserSelectComponent } from './components/user-select/user-select.component';
import { SaleButtonComponent } from './components/sale-button/sale-button.component';
import { DndModule } from 'ngx-drag-drop';
import { BrowserModule } from '@angular/platform-browser';
import { LocaleSelectComponent } from './components/locale-select/locale-select.component';
import { LocalesSelectComponent } from './components/locales-select/locales-select.component';
import { TypeBadgeComponent } from './components/type-badge/type-badge.component';
// TODO: needs to be swapped out with (?) @angular-googlemaps
// import { AgmDirectionModule } from 'agm-direction';
import { GoogleMap } from '@angular/google-maps';

import { LinksComponent } from './components/links/links.component';
import { LinksButtonComponent } from './components/links-button/links-button.component';
import { MapModalComponent } from '@app/shared/components/map-modal/map-modal.component';
import { TagsSelectComponent } from './components/tags-select/tags-select.component';
import { KendoCitySelectComponent } from '@app/shared/kendo/filters/kendo-city-select/kendo-city-select.component';
import { KendoCountrySelectComponent } from './kendo/filters/kendo-country-select/kendo-country-select.component';
import { KendoTypeSelectComponent } from './kendo/filters/kendo-type-select/kendo-type-select.component';
import { KendoCompanySelectComponent } from './kendo/filters/kendo-company-select/kendo-company-select.component';
import { KendoSupplierSelectComponent } from './kendo/filters/kendo-supplier-select/kendo-supplier-select.component';
import { KendoHotelSelectComponent } from './kendo/filters/kendo-hotel-select/kendo-hotel-select.component';
import { KendoIndustrySelectComponent } from './kendo/filters/kendo-industry-select/kendo-industry-select.component';
import { KendoEventGroupSelectComponent } from './kendo/filters/kendo-event-group-select/kendo-event-group-select.component';
import { KendoEventSelectComponent } from './kendo/filters/kendo-event-select/kendo-event-select.component';
import { KendoVenueSelectComponent } from './kendo/filters/kendo-venue-select/kendo-venue-select.component';
import { KendoClientSelectComponent } from './kendo/filters/kendo-client-select/kendo-client-select.component';
import { KendoUserSelectComponent } from './kendo/filters/kendo-user-select/kendo-user-select.component';
import { LinksCompactComponent } from './components/links-compact/links-compact.component';
import { BankAccountSelectComponent } from './components/bank-account-select/bank-account-select.component';
import { UserStatesComponent } from './components/user-states/user-states.component';
import { PaymentButtonComponent } from './components/payment-button/payment-button.component';
import { ClientableSelectComponent } from './components/clientable-select/clientable-select.component';
import { CompaniesSelectComponent } from './components/companies-select/companies-select.component';
import { ContactsBoxComponent } from './components/contacts-box/contacts-box.component';
import { RouterModule } from '@angular/router';
import { HotelFinderComponent } from './components/hotel-finder/hotel-finder.component';
import { KendoGridRefreshButtonComponent } from './components/kendo-grid-refresh-button/kendo-grid-refresh-button.component';
import { ClientBoxComponent } from './components/client-box/client-box.component';
import { CountryBoxComponent } from './components/country-box/country-box.component';
import { CurrencyBoxComponent } from './components/currency-box/currency-box.component';
import { ClientHistoryComponent } from './components/client-history/client-history.component';
import { IpsSelectComponent } from './components/ips-select/ips-select.component';
import { DownloadRoomingListButtonComponent } from './components/download-rooming-list-button/download-rooming-list-button.component';
import { SmtpSettingsComponent } from './components/smtp-settings/smtp-settings.component';
import { BankAccountsBoxComponent } from './components/bank-accounts-box/bank-accounts-box.component';
import { SideButtonComponent } from './components/side-button/side-button.component';
import { ReminderButtonComponent } from './components/reminder-button/reminder-button.component';
import { DatetimePickerComponent } from './components/datetime-picker/datetime-picker.component';
import { UserAndGroupSelectComponent } from './components/user-and-group-select/user-and-group-select.component';
import { KendoTypesSelectComponent } from './kendo/filters/kendo-types-select/kendo-types-select.component';
import { DownloadVouchersButtonComponent } from './components/download-vouchers-button/download-vouchers-button.component';
import { TimezoneSelectComponent } from './components/timezone-select/timezone-select.component';
import { ReminderViewButtonComponent } from './components/reminder-view-button/reminder-view-button.component';
import { CodemirrorModule } from '@ctrl/ngx-codemirror';
import { NgxJsonViewerModule } from 'ngx-json-viewer';
import { AllotmentFinderComponent } from './components/allotment-finder/allotment-finder.component';
import { EmailTemplateSenderComponent } from './components/email-template-sender/email-template-sender.component';
import { MinNightsPickerComponent } from './components/min-nights-picker/min-nights-picker.component';
import { InvoicesSelectComponent } from './components/invoices-select/invoices-select.component';
import { OfferMultipleClientsComponent } from './components/offer-multiple-clients/offer-multiple-clients.component';
import { KendoEventsSelectComponent } from './kendo/filters/kendo-events-select/kendo-events-select.component';
import { EditorModule } from '@tinymce/tinymce-angular';
import { EmailTemplatesButtonComponent } from './components/email-templates-button/email-templates-button.component';
import { ImageInputComponent } from './components/image-input/image-input.component';
import { AngularDraggableModule } from 'angular2-draggable';
import { EditorComponent } from './components/editor/editor.component';
import { ClientEmailsComponent } from './components/exports-buttons/client-emails/client-emails.component';
import { QuickSetAssignAndDateComponent } from './components/quick-set-assign-and-date/quick-set-assign-and-date.component';
import { HotelBookingsMassReassignComponent } from './components/hotel-bookings-mass-reassign/hotel-bookings-mass-reassign.component';
import { DownloadInvoiceButtonComponent } from './components/download-invoice-button/download-invoice-button.component';
import { LinksCompactTranslatesComponent } from './components/links-compact-translates/links-compact-translates.component';
import Swal from 'sweetalert2';
import { CommentViewButtonComponent } from './components/comment-view-button/comment-view-button.component';
import { KendoTagSelectComponent } from './kendo/filters/kendo-tag-select/kendo-tag-select.component';
import { KendoUniversalInputComponent } from './kendo/filters/kendo-universal-input/kendo-universal-input.component';
import { PhoneCallsFinderComponent } from './components/phone-calls-finder/phone-calls-finder.component';
import { PhoneCallsFinderButtonComponent } from './components/phone-calls-finder-button/phone-calls-finder-button.component';
import { SaleMassReassignComponent } from './components/sale-mass-reassign/sale-mass-reassign.component';
import { KendoUniversalBooleanSelectComponent } from './kendo/filters/kendo-universal-boolean-select/kendo-universal-boolean-select.component';
import { IndustriesSelectComponent } from '@app/shared/components/industries-select/industries-select.component';
import { KendoRoleSelectComponent } from '@app/shared/kendo/filters/kendo-role-select/kendo-role-select.component';
import { RoleSelectComponent } from '@app/shared/components/role-select/role-select.component';
import { Ng2SearchPipeModule } from 'ng2-search-filter';
// import { MatGoogleMapsAutocompleteModule } from '@angular-material-extensions/google-maps-autocomplete';
import { IntlService } from '@progress/kendo-angular-intl';
import { MyIntlService } from './services/my-intl.service';

@NgModule({
  imports: [
    CommonModule,
    MomentModule,
    GridModule,
    ExcelModule,
    NgSelectModule,
    FormsModule,
    ReactiveFormsModule,
    ToastNoAnimationModule.forRoot({
      closeButton: true,
      progressBar: true,
    }),
    TranslateModule.forRoot(),
    SweetAlert2Module.forRoot({
      provideSwal: Swal.mixin({
        titleText: 'Delete?',
        text: 'Are you sure?',
        showCancelButton: true,
        confirmButtonColor: 'red',
      }),
    }),
    SidebarModule,
    DataViewModule,
    BrowserAnimationsModule,
    NgScrollbarModule,
    DiffMatchPatchModule,
    FileUploadModule,
    InputTextareaModule,
    // TODO: use google maps api key
    // AgmCoreModule.forRoot({
    //   apiKey: 'AIzaSyAQ-azlvtBFJUOqyQq1Y9oZgr1_vntpJAQ',
    //   libraries: ['places'],
    // }),
    NgxGoogleAddressAutocompleteModule,
    GoogleMapsModule,
    NgbTooltipModule,
    CalendarModule,
    TabViewModule,
    PaginatorModule,
    AngularDraggableModule,
    NgxFilesizeModule,
    // Ng2SearchPipeModule,
    NgbPopoverModule,
    AccordionModule,
    DndModule,
    BrowserModule,
    // TODO: for replacement
    // AgmDirectionModule,
    RouterModule,
    NgbModule,
    CodemirrorModule,
    EditorModule,
    NgxJsonViewerModule,
    AutoCompleteModule,
    ColorPickerModule,
    // Ng2SearchPipeModule,
    // MatGoogleMapsAutocompleteModule
  ],
  declarations: [
    LoaderComponent,
    FormValidationMessageComponent,
    EditButtonComponent,
    DeleteButtonComponent,
    ActiveIndicatorComponent,
    CountriesSelectComponent,
    ActivitiesComponent,
    MediaLibraryComponent,
    MediaLibraryButtonComponent,
    ContactPersonComponent,
    ContactComponent,
    TypeSelectComponent,
    PropertyComponent,
    CitiesSelectComponent,
    BankAccountsComponent,
    VenueSelectComponent,
    VenueDistanceComponent,
    VenuesInRangeComponent,
    IndustrySelectComponent,
    DatePickerComponent,
    EventGroupSelectComponent,
    HotelSelectComponent,
    SupplierSelectComponent,
    CompanySelectComponent,
    EventsSelectComponent,
    CurrencySelectComponent,
    CurrencyConverterComponent,
    NightStatusComponent,
    CommentsComponent,
    ActivitiesButtonComponent,
    TimePickerComponent,
    UserBoxComponent,
    ClientSelectComponent,
    ClientFinderComponent,
    SeoButtonComponent,
    PreviewButtonComponent,
    EventSelectComponent,
    UserSelectComponent,
    SaleButtonComponent,
    LocaleSelectComponent,
    LocalesSelectComponent,
    TypeBadgeComponent,
    LinksComponent,
    LinksButtonComponent,
    MapModalComponent,
    TagsSelectComponent,
    KendoCitySelectComponent,
    KendoRoleSelectComponent,
    RoleSelectComponent,
    KendoCountrySelectComponent,
    KendoTypeSelectComponent,
    KendoCompanySelectComponent,
    KendoSupplierSelectComponent,
    KendoHotelSelectComponent,
    KendoIndustrySelectComponent,
    KendoEventGroupSelectComponent,
    KendoEventSelectComponent,
    KendoVenueSelectComponent,
    KendoClientSelectComponent,
    KendoUserSelectComponent,
    LinksCompactComponent,
    BankAccountSelectComponent,
    UserStatesComponent,
    PaymentButtonComponent,
    ClientableSelectComponent,
    CompaniesSelectComponent,
    ContactsBoxComponent,
    HotelFinderComponent,
    KendoGridRefreshButtonComponent,
    ClientBoxComponent,
    CountryBoxComponent,
    CurrencyBoxComponent,
    ClientHistoryComponent,
    IpsSelectComponent,
    DownloadRoomingListButtonComponent,
    SmtpSettingsComponent,
    BankAccountsBoxComponent,
    SideButtonComponent,
    ReminderButtonComponent,
    DatetimePickerComponent,
    UserAndGroupSelectComponent,
    KendoTypesSelectComponent,
    DownloadVouchersButtonComponent,
    TimezoneSelectComponent,
    ReminderViewButtonComponent,
    AllotmentFinderComponent,
    EmailTemplateSenderComponent,
    MinNightsPickerComponent,
    InvoicesSelectComponent,
    OfferMultipleClientsComponent,
    KendoEventsSelectComponent,
    EmailTemplatesButtonComponent,
    ImageInputComponent,
    EditorComponent,
    ClientEmailsComponent,
    QuickSetAssignAndDateComponent,
    HotelBookingsMassReassignComponent,
    DownloadInvoiceButtonComponent,
    LinksCompactTranslatesComponent,
    CommentViewButtonComponent,
    KendoTagSelectComponent,
    KendoUniversalInputComponent,
    PhoneCallsFinderComponent,
    PhoneCallsFinderButtonComponent,
    SaleMassReassignComponent,
    KendoUniversalBooleanSelectComponent,
    IndustriesSelectComponent,
  ],
  providers: [MediaLibraryService, KendoGridService, AuthenticationService, {
    provide: IntlService,
    useClass: MyIntlService
  }],
  exports: [
    LoaderComponent,
    EditButtonComponent,
    FormValidationMessageComponent,
    MomentModule,
    GridModule,
    ExcelModule,
    ToastNoAnimationModule,
    TranslateModule,
    SweetAlert2Module,
    DeleteButtonComponent,
    AngularDraggableModule,
    ActiveIndicatorComponent,
    CountriesSelectComponent,
    NgSelectModule,
    FormsModule,
    ReactiveFormsModule,
    ActivitiesComponent,
    SidebarModule,
    BrowserAnimationsModule,
    FileUploadModule,
    MediaLibraryButtonComponent,
    NgScrollbarModule,
    InputTextareaModule,
    ContactPersonComponent,
    ContactComponent,
    TypeSelectComponent,
    PropertyComponent,
    GoogleMapsModule,
    CitiesSelectComponent,
    NgxGoogleAddressAutocompleteModule,
    BankAccountsComponent,
    VenueSelectComponent,
    VenueDistanceComponent,
    VenuesInRangeComponent,
    NgbTooltipModule,
    IndustrySelectComponent,
    CalendarModule,
    DatePickerComponent,
    EventGroupSelectComponent,
    HotelSelectComponent,
    SupplierSelectComponent,
    CompanySelectComponent,
    EventsSelectComponent,
    CurrencySelectComponent,
    CurrencyConverterComponent,
    NightStatusComponent,
    CommentsComponent,
    ActivitiesButtonComponent,
    PaginatorModule,
    TimePickerComponent,
    UserBoxComponent,
    ClientSelectComponent,
    ClientFinderComponent,
    SeoButtonComponent,
    PreviewButtonComponent,
    EventSelectComponent,
    UserSelectComponent,
    SaleButtonComponent,
    NgbPopoverModule,
    AccordionModule,
    DndModule,
    BrowserModule,
    LocaleSelectComponent,
    LocalesSelectComponent,
    TypeBadgeComponent,
    // AgmDirectionModule,
    LinksComponent,
    LinksButtonComponent,
    MapModalComponent,
    TagsSelectComponent,
    KendoCitySelectComponent,
    KendoRoleSelectComponent,
    RoleSelectComponent,
    KendoCountrySelectComponent,
    KendoTypeSelectComponent,
    KendoCompanySelectComponent,
    KendoSupplierSelectComponent,
    KendoHotelSelectComponent,
    KendoIndustrySelectComponent,
    KendoEventGroupSelectComponent,
    KendoEventSelectComponent,
    KendoVenueSelectComponent,
    KendoClientSelectComponent,
    KendoUserSelectComponent,
    LinksCompactComponent,
    BankAccountSelectComponent,
    UserStatesComponent,
    PaymentButtonComponent,
    ClientableSelectComponent,
    CompaniesSelectComponent,
    ContactsBoxComponent,
    HotelFinderComponent,
    KendoGridRefreshButtonComponent,
    ClientBoxComponent,
    CountryBoxComponent,
    CurrencyBoxComponent,
    ClientHistoryComponent,
    IpsSelectComponent,
    DownloadRoomingListButtonComponent,
    SmtpSettingsComponent,
    BankAccountsBoxComponent,
    SideButtonComponent,
    ReminderButtonComponent,
    DatetimePickerComponent,
    UserAndGroupSelectComponent,
    KendoTypesSelectComponent,
    DownloadVouchersButtonComponent,
    TimezoneSelectComponent,
    ReminderViewButtonComponent,
    CodemirrorModule,
    EditorModule,
    NgxJsonViewerModule,
    AllotmentFinderComponent,
    EmailTemplateSenderComponent,
    MinNightsPickerComponent,
    InvoicesSelectComponent,
    OfferMultipleClientsComponent,
    EmailTemplatesButtonComponent,
    AutoCompleteModule,
    ImageInputComponent,
    EditorComponent,
    ClientEmailsComponent,
    QuickSetAssignAndDateComponent,
    DownloadInvoiceButtonComponent,
    NgbModule,
    HotelBookingsMassReassignComponent,
    LinksCompactTranslatesComponent,
    CommentViewButtonComponent,
    KendoTagSelectComponent,
    KendoUniversalInputComponent,
    PhoneCallsFinderComponent,
    PhoneCallsFinderButtonComponent,
    SaleMassReassignComponent,
    KendoUniversalBooleanSelectComponent,
    IndustriesSelectComponent,
    // MatGoogleMapsAutocompleteModule
  ],
})
export class SharedModule { }
